import React from "react";
import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import {
  Box,
  Toolbar,
  Typography,
  Menu,
  MenuItem,
  LinearProgress,
  Paper,
  Fade,
  Divider,
  Stack,
  Tooltip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useRecoilValue } from "recoil";
import CircleImage from "./atoms/CircleImage";
import logoImage from "../assets/Logo.png";
import { RoleString, uploadUserAvatar } from "../data/UserData";
import userDataState from "../atoms/userDataAtom";
import organizationDataState from "../atoms/organizationDataAtom";
import PersonIcon from "@mui/icons-material/Person";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import LogoutIcon from "@mui/icons-material/Logout";
import { getTierDisplayName } from "../utils/subscriptionUtils";
import { Chip } from "@mui/material";
import useCoachSignOut from "../hooks/useCoachSignOut";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import { toggleCoachConsoleDocumentIdsVisible } from "../utils/coachConsoleDocumentIds";
import consola from "consola";

const MainHeader = ({ onUpdate }) => {
  const navigate = useNavigate();
  const handleSignOut = useCoachSignOut();

  const userData = useRecoilValue(userDataState);
  const organizationData = useRecoilValue(organizationDataState);

  const [anchorEl, setAnchorEl] = React.useState(null);
  const [updatingAvatar, setUpdatingAvatar] = React.useState(false);
  const [avatarUploadProgress, setAvatarUploadProgress] = React.useState(0);

  // Create a ref for the file input to programmatically trigger it
  const fileInputRef = React.useRef(null);
  const logoMultiClickRef = React.useRef({ count: 0, timer: null });

  const handleClick = (event) => {
    // Prevent any potential event bubbling issues
    event.stopPropagation();
    // Set the anchor element for the menu positioning
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const onSignOutClick = async (methodType) => {
    setAnchorEl(null);
    await handleSignOut(methodType);
  };

  const handleChangeAvatar = async (e) => {
    try {
      // Clear file input value first
      e.stopPropagation();
      setAnchorEl(null);

      // Check if file is selected
      if (e.target && e.target.files && e.target.files[0]) {
        const image = e.target.files[0];

        // Validate file type
        const validTypes = ["image/jpeg", "image/jpg", "image/png"];
        if (!validTypes.includes(image.type)) {
          console.error("Invalid file type:", image.type);
          alert("Please select a JPG or PNG image");
          // Reset file input
          if (fileInputRef.current) {
            fileInputRef.current.value = "";
          }
          return;
        }

        // Validate file size (max 5MB)
        const maxSize = 5 * 1024 * 1024; // 5MB
        if (image.size > maxSize) {
          console.error("File too large:", image.size);
          alert("Image must be smaller than 5MB");
          // Reset file input
          if (fileInputRef.current) {
            fileInputRef.current.value = "";
          }
          return;
        }

        // Start upload process
        setUpdatingAvatar(true);
        setAvatarUploadProgress(0);

        // Upload avatar
        await uploadUserAvatar({
          uid: userData.uid,
          file: image,
          progressFunction: (progress) => {
            setAvatarUploadProgress(progress);
          },
        });

        // Reset states after successful upload
        setUpdatingAvatar(false);
        setAvatarUploadProgress(0);

        // Reset file input for future uploads
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }

        // Notify parent component to update
        if (onUpdate) onUpdate(new Date());
      }
    } catch (error) {
      console.error("Error changing avatar:", error);
      setUpdatingAvatar(false);
      setAvatarUploadProgress(0);

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      alert("Failed to update profile picture. Please try again.");
    }
  };

  // Progress bar is now rendered directly in the component

  return (
    <Paper
      elevation={2}
      sx={{
        background: "linear-gradient(to right, #ffffff, #f7f9fc)",
        borderRadius: 0,
        position: "relative",
      }}
    >
      <Toolbar sx={{ py: 1.5, px: { xs: 2, md: 3 }, position: "relative" }}>
        <Box
          sx={{
            width: "100%",
            alignItems: "center",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          {/* Logo Section */}
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <img
              alt="Mighty45 Logo"
              style={{
                cursor: "pointer",
                transition: "transform 0.2s ease",
              }}
              width="130px"
              height="70px"
              src={logoImage}
              onClick={() => {
                navigate("/");
                const ref = logoMultiClickRef.current;
                ref.count += 1;
                if (ref.timer) clearTimeout(ref.timer);
                ref.timer = setTimeout(() => {
                  if (ref.count >= 3) {
                    const on = toggleCoachConsoleDocumentIdsVisible();
                    consola.info(
                      `[Coach Console] Document IDs in UI: ${on ? "ON" : "OFF"}`,
                    );
                  }
                  ref.count = 0;
                  ref.timer = null;
                }, 600);
              }}
              onMouseOver={(e) =>
                (e.currentTarget.style.transform = "scale(1.02)")
              }
              onMouseOut={(e) => (e.currentTarget.style.transform = "scale(1)")}
            />
          </Box>

          {/* User Info Section */}
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{
              background: "rgba(255,255,255,0.8)",
              borderRadius: 2,
              py: 1,
              px: 2,
              boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
              position: "relative",
            }}
          >
            <Stack spacing={0.5} alignItems="flex-end">
              <Typography
                variant="subtitle1"
                fontWeight="500"
                color="text.primary"
                sx={{ lineHeight: 1.2 }}
              >
                {userData ? `${userData.firstName} ${userData.lastName}` : ""}
              </Typography>
              <Typography
                variant="caption"
                fontWeight="600"
                color="primary.main"
                sx={{ lineHeight: 1.2 }}
              >
                {userData && userData.role
                  ? `${RoleString[userData.role]}`
                  : ""}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ lineHeight: 1.2 }}
              >
                {organizationData ? organizationData.name : ""}
              </Typography>
              {organizationData && (
                <Chip
                  label={getTierDisplayName(
                    organizationData.account?.accountType || "free"
                  )}
                  size="small"
                  color={
                    (organizationData.account?.accountType || "free") === "free"
                      ? "default"
                      : (organizationData.account?.accountType || "free") === "pro"
                      ? "primary"
                      : "secondary"
                  }
                  onClick={() => navigate("/subscribe")}
                  sx={{
                    mt: 0.5,
                    cursor: "pointer",
                    fontSize: "0.7rem",
                    height: "20px",
                  }}
                />
              )}
            </Stack>

            <Box
              sx={{
                position: "relative",
                cursor: "pointer",
                "&:hover": {
                  "& .avatar-circle": {
                    transform: "scale(1.05)",
                    boxShadow: "0 5px 15px rgba(0,0,0,0.2)",
                  },
                  "& .avatar-overlay": {
                    opacity: 1,
                  },
                },
                // Add z-index to ensure avatar is clickable
                zIndex: 100,
              }}
              onClick={handleClick}
              data-testid="avatar-menu-trigger"
            >
              <Tooltip title="Click for account menu" arrow placement="bottom">
                <Box
                  className="avatar-circle"
                  sx={{
                    position: "relative",
                    transition: "transform 0.2s ease, box-shadow 0.2s ease",
                    boxShadow: "0 3px 10px rgba(0,0,0,0.1)",
                    borderRadius: "50%", // Make the container round
                    overflow: "hidden", // Ensure anything outside the circle is hidden
                  }}
                >
                  <CircleImage
                    firstName={userData ? userData.firstName : ""}
                    lastName={userData ? userData.lastName : ""}
                    size={50}
                    url={
                      userData && userData.avatarUrl ? userData.avatarUrl : null
                    }
                    // Add onClick handler directly to CircleImage as well for better click capture
                    onClick={(e) => {
                      e.stopPropagation();
                      handleClick(e);
                    }}
                  />
                  <Box
                    className="avatar-overlay"
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      borderRadius: "50%",
                      backgroundColor: "rgba(0, 0, 0, 0.3)",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      opacity: 0,
                      transition: "opacity 0.2s ease",
                      cursor: "pointer",
                      zIndex: 2,
                      overflow: "hidden", // Ensure overlay respects circular boundaries
                    }}
                  >
                    <PersonIcon sx={{ color: "white" }} />
                  </Box>
                </Box>
              </Tooltip>
              {updatingAvatar && (
                <LinearProgress
                  style={{
                    marginTop: 2,
                    borderRadius: 5,
                    height: 6,
                    width: "100%",
                  }}
                  variant="determinate"
                  value={avatarUploadProgress}
                />
              )}
            </Box>
          </Stack>
        </Box>

        {/* Enhanced Menu */}
        <Menu
          id="user-menu"
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          TransitionComponent={Fade}
          keepMounted
          // Use the portal to ensure the menu appears above all other elements
          disablePortal={false}
          transitionDuration={200}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          slotProps={{
            backdrop: {
              invisible: true,
            },
          }}
          MenuListProps={{
            sx: { py: 1 },
            // Add autoFocus to make menu items interactive immediately
            autoFocus: true,
            // Add additional keyboard handling
            onKeyDown: (e) => {
              if (e.key === "Escape") {
                handleClose();
              }
            },
            // Add data-testid for easier testing
            "data-testid": "user-menu-list",
          }}
          PopoverClasses={{
            root: "user-menu-popover",
          }}
          sx={{
            // Ensure the menu is at the top of the stacking order
            zIndex: 10000,
            pointerEvents: "all",
          }}
          PaperProps={{
            elevation: 5,
            sx: {
              overflow: "visible",
              zIndex: 10000,
              filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.15))",
              borderRadius: 2,
              minWidth: 200,
              mt: 1,
              pointerEvents: "auto",
              "& .MuiMenuItem-root": {
                px: 2,
                py: 1.5,
                borderRadius: 1,
                mx: 0.5,
                my: 0.3,
                fontSize: "0.95rem",
                cursor: "pointer",
              },
              "&::before": {
                content: '""',
                display: "block",
                position: "absolute",
                top: -6,
                right: 14,
                width: 12,
                height: 12,
                bgcolor: "background.paper",
                transform: "rotate(45deg)",
                zIndex: 0,
              },
            },
          }}
        >
          <MenuItem
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setAnchorEl(null);
              navigate("/home/profile");
            }}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              fontWeight: 500,
              "&:hover": {
                bgcolor: "action.hover",
              },
            }}
            data-testid="profile-menu-item"
          >
            <AccountCircleIcon fontSize="small" color="primary" />
            <Typography>Profile</Typography>
          </MenuItem>
          <MenuItem
            onClick={(e) => {
              e.stopPropagation(); // Prevent event bubbling
              e.preventDefault(); // Prevent default action
              // Close menu first to avoid visual issues
              setAnchorEl(null);
              // Use setTimeout to ensure menu is closed before opening file dialog
              setTimeout(() => {
                if (fileInputRef.current) {
                  fileInputRef.current.click();
                }
              }, 100);
            }}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              fontWeight: 500,
              "&:hover": {
                bgcolor: "action.hover",
              },
            }}
            data-testid="change-avatar-menu-item"
          >
            <PhotoCameraIcon fontSize="small" color="primary" />
            <Typography>Change avatar</Typography>
          </MenuItem>
          <input
            ref={fileInputRef}
            hidden
            type="file"
            accept=".jpg,.jpeg,.png"
            onChange={handleChangeAvatar}
            onClick={(e) => e.stopPropagation()} // Prevent click propagation
            data-testid="avatar-file-input"
          />

          <Divider sx={{ my: 0.8 }} />

          <MenuItem
            onClick={(e) => {
              e.stopPropagation(); // Prevent event bubbling
              e.preventDefault(); // Prevent default action
              onSignOutClick("Menu Signout Click");
            }}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              fontWeight: 500,
              "&:hover": {
                bgcolor: "error.light",
                color: "error.contrastText",
              },
            }}
            data-testid="sign-out-menu-item"
          >
            <LogoutIcon fontSize="small" color="error" />
            <Typography>Sign out</Typography>
          </MenuItem>
        </Menu>
      </Toolbar>
    </Paper>
  );
};

export default MainHeader;
